import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import creditNoteService from '../services/creditNoteService';
import orderService from '../services/orderService';
import toast from 'react-hot-toast';
import {
  HiOutlineDocumentDuplicate,
  HiOutlinePlusCircle,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineEye,
  HiOutlineExclamation,
  HiOutlineSearch,
  HiOutlineX,
  HiOutlinePrinter,
} from 'react-icons/hi';
import './CreditNotes.css';

const fmt = (n) => Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 });

const CreditNotes = () => {
  const { hasRole } = useAuth();
  const [creditNotes, setCreditNotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  // Modal crear NC
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderResults, setOrderResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [ncType, setNcType] = useState('partial');
  const [reason, setReason] = useState('');
  const [returnStock, setReturnStock] = useState(true);
  const [creating, setCreating] = useState(false);

  // Modal detalle
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // Modal confirmar
  const [confirmModal, setConfirmModal] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cnRes, sumRes] = await Promise.all([
        creditNoteService.getAll({ status: filterStatus || undefined }),
        creditNoteService.getSummary(),
      ]);
      setCreditNotes(cnRes.data || []);
      setSummary(sumRes.data || null);
    } catch {
      toast.error('Error cargando notas de crédito');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Buscar órdenes cerradas con NCF ──
  const searchOrders = async (query) => {
    setOrderSearch(query);
    if (query.length < 2) { setOrderResults([]); return; }
    try {
      const res = await orderService.getAll({ status: 'closed', limit: 10 });
      const orders = res.data || [];
      const filtered = orders.filter(o =>
        o.order_number.toLowerCase().includes(query.toLowerCase()) ||
        (o.client_name || '').toLowerCase().includes(query.toLowerCase()) ||
        (o.client_company || '').toLowerCase().includes(query.toLowerCase())
      );
      setOrderResults(filtered);
    } catch { /* silenciar */ }
  };

  const selectOrder = async (order) => {
    setSelectedOrder(order);
    setOrderSearch(order.order_number);
    setOrderResults([]);
    try {
      const res = await creditNoteService.getAvailableItems(order.id);
      const items = res.data || [];
      setAvailableItems(items);
      // Pre-seleccionar todos si es total
      if (ncType === 'total') {
        const sel = {};
        items.forEach(item => {
          if (item.max_returnable > 0) {
            sel[item.order_item_id] = item.max_returnable;
          }
        });
        setSelectedItems(sel);
      } else {
        setSelectedItems({});
      }
    } catch {
      toast.error('Error cargando ítems de la orden');
    }
  };

  // Al cambiar tipo, actualizar selección
  const handleTypeChange = (type) => {
    setNcType(type);
    if (type === 'total' && availableItems.length > 0) {
      const sel = {};
      availableItems.forEach(item => {
        if (item.max_returnable > 0) {
          sel[item.order_item_id] = item.max_returnable;
        }
      });
      setSelectedItems(sel);
    }
  };

  const toggleItem = (itemId, maxQty) => {
    setSelectedItems(prev => {
      if (prev[itemId] !== undefined) {
        const copy = { ...prev };
        delete copy[itemId];
        return copy;
      }
      return { ...prev, [itemId]: maxQty };
    });
  };

  const updateItemQty = (itemId, qty) => {
    setSelectedItems(prev => ({ ...prev, [itemId]: Math.max(1, qty) }));
  };

  // Calcular totales
  const calcTotals = () => {
    let subtotal = 0;
    availableItems.forEach(item => {
      const qty = selectedItems[item.order_item_id];
      if (qty) subtotal += qty * Number(item.unit_price);
    });
    // ITBIS 18% solo si la orden original requería NCF
    const hasNcf = selectedOrder?.requires_ncf;
    const taxRate = hasNcf ? 0.18 : 0;
    const tax = Math.round(subtotal * taxRate * 100) / 100;
    return { subtotal, tax, total: subtotal + tax, hasNcf };
  };

  // ── Crear NC ──
  const handleCreate = async () => {
    if (!selectedOrder) return toast.error('Seleccione una orden');
    if (!reason || reason.trim().length < 5) return toast.error('Indique un motivo (mín. 5 caracteres)');
    const itemsArr = Object.entries(selectedItems);
    if (itemsArr.length === 0) return toast.error('Seleccione al menos un producto');

    setCreating(true);
    try {
      const items = itemsArr.map(([orderItemId, qty]) => {
        const avItem = availableItems.find(ai => ai.order_item_id === Number(orderItemId));
        return {
          order_item_id: Number(orderItemId),
          product_id: avItem.product_id,
          quantity: qty,
          unit_price: Number(avItem.unit_price),
        };
      });

      await creditNoteService.create({
        order_id: selectedOrder.id,
        reason: reason.trim(),
        type: ncType,
        items,
        return_stock: returnStock,
      });
      toast.success('Nota de crédito creada en borrador');
      closeCreateModal();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear nota de crédito');
    } finally {
      setCreating(false);
    }
  };

  // ── Confirmar NC ──
  const handleConfirm = async (id) => {
    try {
      const res = await creditNoteService.confirm(id);
      toast.success(`Nota de crédito confirmada — NCF: ${res.data?.ncf_number || 'B04'}`);
      setConfirmModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al confirmar');
    }
  };

  // ── Cancelar NC ──
  const handleCancel = async (id) => {
    try {
      await creditNoteService.cancel(id, 'Cancelada por administrador');
      toast.success('Nota de crédito cancelada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cancelar');
    }
  };

  // ── Ver detalle ──
  const viewDetail = async (id) => {
    try {
      const res = await creditNoteService.getById(id);
      setDetailData(res.data);
      setShowDetailModal(true);
    } catch {
      toast.error('Error cargando detalle');
    }
  };

  // ── Imprimir nota de crédito ──
  const handlePrint = async (id) => {
    try {
      const html = await creditNoteService.print(id, 'html');
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
      } else {
        toast.error('El navegador bloqueó la ventana emergente. Permite pop-ups para imprimir.');
      }
    } catch {
      toast.error('Error al generar impresión');
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setSelectedOrder(null);
    setOrderSearch('');
    setOrderResults([]);
    setAvailableItems([]);
    setSelectedItems({});
    setNcType('partial');
    setReason('');
    setReturnStock(true);
  };

  const totals = calcTotals();

  return (
    <div className="page-container credit-notes-page">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>
            <HiOutlineDocumentDuplicate style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Notas de Crédito
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
            Gestión de NCF B04 — Anulaciones parciales o totales de facturas
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={fetchData} className="btn-secondary"
            style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <HiOutlineRefresh /> Actualizar
          </button>
          {hasRole('admin', 'accountant', 'manager') && (
            <button onClick={() => setShowCreateModal(true)}
              style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: '#14532d', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
              <HiOutlinePlusCircle size={18} /> Nueva NC
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="cn-summary-grid">
          <div className="cn-summary-card cn-count">
            <span className="summary-label">NC Confirmadas</span>
            <span className="summary-value">{summary.total_count}</span>
          </div>
          <div className="cn-summary-card cn-tax">
            <span className="summary-label">ITBIS Devuelto</span>
            <span className="summary-value">RD$ {fmt(summary.total_tax)}</span>
          </div>
          <div className="cn-summary-card cn-total">
            <span className="summary-label">Total Acreditado</span>
            <span className="summary-value">RD$ {fmt(summary.total_amount)}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="cn-filters">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="confirmed">Confirmada</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>Cargando...</div>
      ) : creditNotes.length === 0 ? (
        <div className="cn-empty">
          <HiOutlineDocumentDuplicate size={48} />
          <p>No hay notas de crédito</p>
        </div>
      ) : (
        <div className="cn-table-wrapper">
          <table className="cn-table">
            <thead>
              <tr>
                <th>Número</th>
                <th>NCF B04</th>
                <th>Orden / NCF Original</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Motivo</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {creditNotes.map(cn => (
                <tr key={cn.id}>
                  <td className="cn-number">{cn.credit_note_number}</td>
                  <td className="cn-ncf">{cn.ncf_number || '—'}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{cn.order_number}</div>
                    <div style={{ fontSize: '0.72rem', color: '#8b5cf6', fontFamily: 'monospace' }}>{cn.original_ncf}</div>
                  </td>
                  <td>{cn.client_company || cn.client_name}</td>
                  <td>
                    <span className={`cn-type ${cn.type}`}>
                      {cn.type === 'total' ? 'Total' : 'Parcial'}
                    </span>
                  </td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cn.reason}
                  </td>
                  <td className="cn-amount">RD$ {fmt(cn.total_amount)}</td>
                  <td>
                    <span className={`cn-status ${cn.status}`}>
                      {cn.status === 'draft' ? '📝 Borrador' : cn.status === 'confirmed' ? '✅ Confirmada' : '❌ Cancelada'}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {new Date(cn.created_at).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td>
                    <div className="cn-actions">
                      <button className="btn-sm btn-view" onClick={() => viewDetail(cn.id)}>
                        <HiOutlineEye /> Ver
                      </button>
                      {cn.status === 'confirmed' && (
                        <button className="btn-sm btn-print" onClick={() => handlePrint(cn.id)} title="Imprimir para DGII">
                          <HiOutlinePrinter /> Imprimir
                        </button>
                      )}
                      {cn.status === 'draft' && hasRole('admin', 'accountant') && (
                        <button className="btn-sm btn-confirm" onClick={() => setConfirmModal(cn)}>
                          <HiOutlineCheckCircle /> Confirmar
                        </button>
                      )}
                      {cn.status === 'draft' && hasRole('admin') && (
                        <button className="btn-sm btn-cancel" onClick={() => handleCancel(cn.id)}>
                          <HiOutlineXCircle />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ══════════ Modal: Crear Nota de Crédito ══════════ */}
      {showCreateModal && (
        <div className="cn-modal-overlay" onClick={closeCreateModal}>
          <div className="cn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cn-modal-header">
              <h3>📋 Nueva Nota de Crédito</h3>
              <button onClick={closeCreateModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <HiOutlineX size={20} />
              </button>
            </div>
            <div className="cn-modal-body">
              {/* Buscar orden */}
              <div className="cn-form-group cn-order-search">
                <label>Orden con NCF (buscar por número o cliente)</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={orderSearch}
                    onChange={(e) => searchOrders(e.target.value)}
                    placeholder="Ej: ORD-20260207 o nombre del cliente..."
                    style={{ paddingLeft: 36 }}
                  />
                  <HiOutlineSearch style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
                </div>
                {orderResults.length > 0 && (
                  <div className="cn-order-results">
                    {orderResults.map(o => (
                      <div key={o.id} className="cn-order-result-item" onClick={() => selectOrder(o)}>
                        <div className="order-num">
                          {o.order_number}
                          {o.ncf_number ? <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#8b5cf6' }}>{o.ncf_number}</span> : ''}
                        </div>
                        <div className="order-info">
                          {o.client_company || o.client_name} — RD$ {fmt(o.total_amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedOrder && (
                <>
                  <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.82rem' }}>
                    <strong>{selectedOrder.order_number}</strong> — {selectedOrder.client_company || selectedOrder.client_name}
                    <br />
                    <span style={{ color: '#8b5cf6', fontFamily: 'monospace' }}>NCF: {selectedOrder.ncf_number || 'N/A'}</span>
                    {' · '}
                    <span>Total: RD$ {fmt(selectedOrder.total_amount)}</span>
                  </div>

                  {/* Tipo */}
                  <div className="cn-form-group">
                    <label>Tipo de Nota de Crédito</label>
                    <select value={ncType} onChange={(e) => handleTypeChange(e.target.value)}>
                      <option value="partial">Parcial — Devolver algunos productos</option>
                      <option value="total">Total — Anular toda la factura</option>
                    </select>
                  </div>

                  {/* Motivo */}
                  <div className="cn-form-group">
                    <label>Motivo</label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Ej: Cliente devolvió 3 galones de aceite por defecto de empaque..."
                    />
                  </div>

                  {/* Devolver stock */}
                  <div className="cn-form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="returnStock"
                      checked={returnStock}
                      onChange={(e) => setReturnStock(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: '#14532d' }}
                    />
                    <label htmlFor="returnStock" style={{ margin: 0, cursor: 'pointer' }}>
                      Devolver productos al inventario
                    </label>
                  </div>

                  {/* Items */}
                  <div className="cn-form-group">
                    <label>Productos a acreditar</label>
                    <div className="cn-items-list">
                      {availableItems.map(item => {
                        const isDisabled = item.max_returnable <= 0;
                        const isSelected = selectedItems[item.order_item_id] !== undefined;
                        const qty = selectedItems[item.order_item_id] || 0;
                        return (
                          <div key={item.order_item_id} className={`cn-item-row ${isDisabled ? 'disabled' : ''}`}>
                            <input
                              type="checkbox"
                              className="item-check"
                              checked={isSelected}
                              disabled={isDisabled || ncType === 'total'}
                              onChange={() => toggleItem(item.order_item_id, item.max_returnable)}
                            />
                            <div className="item-info">
                              <div className="item-name">{item.product_name}</div>
                              <div className="item-sku">{item.sku}</div>
                            </div>
                            <div className="item-price">RD$ {fmt(item.unit_price)}</div>
                            {isSelected && (
                              <input
                                type="number"
                                className="item-qty-input"
                                min={1}
                                max={item.max_returnable}
                                value={qty}
                                disabled={ncType === 'total'}
                                onChange={(e) => {
                                  const v = Math.min(Number(e.target.value), item.max_returnable);
                                  updateItemQty(item.order_item_id, v);
                                }}
                              />
                            )}
                            <div className="item-max">
                              {isDisabled ? 'Ya devuelto' : `máx: ${item.max_returnable}`}
                            </div>
                            <div className="item-total">
                              {isSelected ? `RD$ ${fmt(qty * Number(item.unit_price))}` : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Totals */}
                  {Object.keys(selectedItems).length > 0 && (
                    <div className="cn-totals">
                      <div className="cn-totals-row">
                        <span className="label">Subtotal a acreditar</span>
                        <span className="value">RD$ {fmt(totals.subtotal)}</span>
                      </div>
                      <div className="cn-totals-row">
                        <span className="label">ITBIS devuelto ({totals.hasNcf ? '18%' : '0% — Sin NCF'})</span>
                        <span className="value">RD$ {fmt(totals.tax)}</span>
                      </div>
                      <div className="cn-totals-row grand">
                        <span className="label">TOTAL NOTA DE CRÉDITO</span>
                        <span className="value">RD$ {fmt(totals.total)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="cn-modal-footer">
              <button onClick={closeCreateModal}
                style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontWeight: 600 }}>
                Cancelar
              </button>
              <button onClick={handleCreate} disabled={creating || !selectedOrder}
                style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: creating ? '#94a3b8' : '#14532d', color: 'white', cursor: creating ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                {creating ? 'Creando...' : '📋 Crear Borrador'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ Modal: Detalle NC ══════════ */}
      {showDetailModal && detailData && (
        <div className="cn-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="cn-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cn-modal-header">
              <h3>Nota de Crédito: {detailData.credit_note_number}</h3>
              <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <HiOutlineX size={20} />
              </button>
            </div>
            <div className="cn-modal-body">
              <div className="cn-detail-section">
                <h4>Información General</h4>
                <div className="cn-detail-grid">
                  <div className="cn-detail-item">
                    <span className="detail-label">NCF B04</span>
                    <span className="detail-value mono">{detailData.ncf_number || 'Pendiente (borrador)'}</span>
                  </div>
                  <div className="cn-detail-item">
                    <span className="detail-label">Estado</span>
                    <span className={`cn-status ${detailData.status}`}>
                      {detailData.status === 'draft' ? '📝 Borrador' : detailData.status === 'confirmed' ? '✅ Confirmada' : '❌ Cancelada'}
                    </span>
                  </div>
                  <div className="cn-detail-item">
                    <span className="detail-label">Orden Original</span>
                    <span className="detail-value">{detailData.order_number}</span>
                  </div>
                  <div className="cn-detail-item">
                    <span className="detail-label">NCF Original</span>
                    <span className="detail-value mono">{detailData.original_ncf}</span>
                  </div>
                  <div className="cn-detail-item">
                    <span className="detail-label">Cliente</span>
                    <span className="detail-value">{detailData.client_company || detailData.client_name}</span>
                  </div>
                  <div className="cn-detail-item">
                    <span className="detail-label">RNC</span>
                    <span className="detail-value mono">{detailData.client_rnc || 'N/A'}</span>
                  </div>
                  <div className="cn-detail-item">
                    <span className="detail-label">Tipo</span>
                    <span className={`cn-type ${detailData.type}`}>
                      {detailData.type === 'total' ? 'Total' : 'Parcial'}
                    </span>
                  </div>
                  <div className="cn-detail-item">
                    <span className="detail-label">Devolver Stock</span>
                    <span className="detail-value">{detailData.return_stock ? '✅ Sí' : '❌ No'}</span>
                  </div>
                </div>
              </div>

              <div className="cn-detail-section">
                <h4>Motivo</h4>
                <p style={{ fontSize: '0.85rem', color: '#475569', margin: 0, padding: '10px 14px', background: '#f8fafc', borderRadius: 10 }}>
                  {detailData.reason}
                </p>
              </div>

              <div className="cn-detail-section">
                <h4>Productos Acreditados</h4>
                <div className="cn-items-list">
                  {(detailData.items || []).map(item => (
                    <div key={item.id} className="cn-item-row">
                      <div className="item-info">
                        <div className="item-name">{item.product_name}</div>
                        <div className="item-sku">{item.sku}</div>
                      </div>
                      <div className="item-price">RD$ {fmt(item.unit_price)} c/u</div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>×{item.quantity}</div>
                      <div className="item-total">RD$ {fmt(item.line_total)}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="cn-totals">
                <div className="cn-totals-row">
                  <span className="label">Subtotal</span>
                  <span className="value">RD$ {fmt(detailData.subtotal)}</span>
                </div>
                <div className="cn-totals-row">
                  <span className="label">ITBIS devuelto</span>
                  <span className="value">RD$ {fmt(detailData.tax_amount)}</span>
                </div>
                <div className="cn-totals-row grand">
                  <span className="label">TOTAL</span>
                  <span className="value">RD$ {fmt(detailData.total_amount)}</span>
                </div>
              </div>

              {detailData.created_by_name && (
                <div style={{ marginTop: 12, fontSize: '0.78rem', color: '#94a3b8' }}>
                  Creada por: {detailData.created_by_name}
                  {detailData.confirmed_by_name && ` · Confirmada por: ${detailData.confirmed_by_name}`}
                </div>
              )}
            </div>
            <div className="cn-modal-footer">
              {detailData.status === 'confirmed' && (
                <button onClick={() => handlePrint(detailData.id)}
                  style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <HiOutlinePrinter size={18} /> Imprimir para DGII
                </button>
              )}
              <button onClick={() => setShowDetailModal(false)}
                style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontWeight: 600 }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ Modal: Confirmar NC ══════════ */}
      {confirmModal && (
        <div className="cn-modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="cn-modal cn-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cn-modal-header">
              <h3>Confirmar Nota de Crédito</h3>
              <button onClick={() => setConfirmModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <HiOutlineX size={20} />
              </button>
            </div>
            <div className="cn-modal-body">
              <HiOutlineExclamation className="confirm-icon" />
              <p className="confirm-text">
                Al confirmar se generará un <strong>NCF B04</strong> irreversible.
                {confirmModal.return_stock && ' Los productos serán devueltos al inventario.'}
              </p>
              <div style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 8 }}>
                {confirmModal.credit_note_number} — {confirmModal.order_number}
              </div>
              <div className="confirm-amount">RD$ {fmt(confirmModal.total_amount)}</div>
            </div>
            <div className="cn-modal-footer" style={{ justifyContent: 'center' }}>
              <button onClick={() => setConfirmModal(null)}
                style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontWeight: 600 }}>
                Cancelar
              </button>
              <button onClick={() => handleConfirm(confirmModal.id)}
                style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: '#14532d', color: 'white', cursor: 'pointer', fontWeight: 700 }}>
                ✅ Confirmar y Generar NCF B04
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditNotes;
